import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { post } from "@/lib/api";
import { date, eur, today } from "@/lib/format";
import type { BuiltPosition, CreateEntriesOutput, PreviewEntriesOutput } from "@/lib/types";

export function InvoiceDialog({ entryIds, open, onClose, onCreated }: { entryIds: number[]; open: boolean; onClose: () => void; onCreated: () => void }) {
  const qc = useQueryClient();
  const [invoiceDate, setInvoiceDate] = useState(today());
  const preview = useQuery({
    queryKey: ["invoice-preview", entryIds],
    queryFn: () => post<PreviewEntriesOutput>("/api/invoices/preview", { entryIds }),
    enabled: open,
    staleTime: 0,
  });
  const create = useMutation({
    mutationFn: () => post<CreateEntriesOutput>("/api/invoices", { entryIds, invoiceDate }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["entries"] });
      // Never show the sevDesk id as if it were the invoice number.
      toast.success(res.sevdeskNumber
        ? `Rechnung ${res.sevdeskNumber} als Entwurf angelegt (${eur(res.total)})`
        : `Entwurf ohne Nummer angelegt (${eur(res.total)})`);
      for (const w of res.warnings) toast.warning(w);
      onCreated();
      onClose();
    },
    onError: (e) => toast.error(e.message),
  });
  const p = preview.data;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader><DialogTitle>Rechnung erstellen</DialogTitle></DialogHeader>
        {preview.isPending && <p className="text-sm text-muted-foreground">Vorschau wird berechnet…</p>}
        {preview.isError && <p className="text-sm text-destructive">{preview.error.message}</p>}
        {p && (
          <div className="space-y-3">
            <p className="text-sm">
              <span className="font-medium">{p.clientName}</span>
              <span className="text-muted-foreground"> · Leistungszeitraum {date(p.from)} – {date(p.to)} · {p.entryCount} Buchungen</span>
            </p>
            <Table>
              <TableHeader>
                <TableRow><TableHead>Position</TableHead><TableHead className="text-right">Menge</TableHead><TableHead className="text-right">Preis</TableHead><TableHead className="text-right">Summe</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(p.positions as BuiltPosition[]).map((pos, i) => (
                  <TableRow key={i}>
                    <TableCell>{pos.name}</TableCell>
                    <TableCell className="text-right tabular-nums">{pos.quantity.toLocaleString("de-DE")}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(pos.price)}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(pos.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow><TableCell colSpan={3}>Gesamt (Kleinunternehmer, keine USt.)</TableCell><TableCell className="text-right tabular-nums">{eur(p.total)}</TableCell></TableRow>
              </TableFooter>
            </Table>
            <div className="flex items-center gap-2">
              <Label htmlFor="invdate">Rechnungsdatum</Label>
              <Input id="invdate" type="date" className="w-44" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button disabled={!p || create.isPending} onClick={() => create.mutate()}>Als Entwurf in sevDesk anlegen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
